import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Form,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useAuth } from "contexts/AuthContext";
import useBackgroundChecks from "hooks/olreadia/useBackgroundChecks";
import { useNavigate } from "react-router-dom";
import paths from "routes/paths";

const { Text } = Typography;

const BACKGROUND_CHECK_TYPES = [
  { value: "Profile validation and verification", label: "Profile validation and verification" },
  { value: "Criminal record checks", label: "Criminal record checks" },
];

const CHECK_STATUS_COLORS = {
  REQUESTED: "gold",
  PENDING: "cyan",
  IN_PROGRESS: "blue",
  PASSED: "green",
  FAILED: "red",
  CANCELED: "default",
};

const toLabel = (value) =>
  value ? String(value).replaceAll("_", " ").replace(/\b\w/g, (match) => match.toUpperCase()) : "-";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("MMM D, YYYY h:mm A") : String(value);
};

const countChecksByStatus = (checks = []) => {
  const list = Array.isArray(checks) ? checks : [];
  const counts = {
    total: list.length,
    requested: 0,
    pending: 0,
    inProgress: 0,
    passed: 0,
    failed: 0,
    canceled: 0,
  };

  list.forEach((check) => {
    const status = String(check?.status || "").toUpperCase();
    if (status === "REQUESTED") counts.requested += 1;
    else if (status === "PENDING") counts.pending += 1;
    else if (status === "IN_PROGRESS") counts.inProgress += 1;
    else if (status === "PASSED") counts.passed += 1;
    else if (status === "FAILED") counts.failed += 1;
    else if (status === "CANCELED") counts.canceled += 1;
  });

  counts.open = counts.requested + counts.pending + counts.inProgress;
  counts.closed = counts.passed + counts.failed + counts.canceled;
  counts.passRate = counts.closed > 0 ? Math.round((counts.passed / counts.closed) * 100) : 0;
  return counts;
};

const StatCard = ({ label, value, hint, tone = "neutral" }) => {
  const palette =
    tone === "success"
      ? { background: "rgba(22,101,52,0.08)", color: "#166534", accent: "rgba(22,101,52,0.16)" }
      : tone === "warning"
      ? { background: "rgba(245,158,11,0.08)", color: "#b45309", accent: "rgba(245,158,11,0.16)" }
      : tone === "danger"
      ? { background: "rgba(239,68,68,0.08)", color: "#b91c1c", accent: "rgba(239,68,68,0.16)" }
      : { background: "rgba(71,85,105,0.06)", color: "#334155", accent: "rgba(71,85,105,0.12)" };

  return (
    <Card
      size="small"
      styles={{ body: { padding: 8 } }}
      style={{
        borderRadius: 12,
        border: "none",
        boxShadow: "none",
        background: `linear-gradient(180deg, ${palette.background} 0%, #fff 100%)`,
      }}
    >
      <Space direction="vertical" size={1} style={{ width: "100%" }}>
        <Text style={{ fontSize: 12, letterSpacing: 0.3, color: "#64748b" }}>{label}</Text>
        <Text strong style={{ fontSize: 13, color: palette.color, lineHeight: 1.1 }}>
          {value}
        </Text>
        {hint ? (
          <Text style={{ fontSize: 12, color: "#64748b", lineHeight: 1.2 }}>{hint}</Text>
        ) : null}
      </Space>
      <div style={{ height: 2, marginTop: 8, borderRadius: 999, background: palette.accent }} />
    </Card>
  );
};

export default function BackgroundChecksPanel({
  profile,
  helperProfile,
  professionProfileId,
  userId,
  profileName,
  sectorLabel,
  createDrawerOpen,
  onCreateDrawerOpenChange,
  onReloadProfile,
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const actorId = user?.id || "";
  const { checks, loading, error, refresh, createForProfessionProfile } = useBackgroundChecks();

  const [createForm] = Form.useForm();
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);

  const checkStats = useMemo(() => countChecksByStatus(checks), [checks]);

  const filteredChecks = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return checks.filter((check) => {
      if (!query) return true;

      const haystack = [
        check?.checkType,
        check?.status,
        check?.notes,
        check?.requestedBy,
        check?.initiatedBy,
        check?.userFullName,
        check?.userEmail,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });
  }, [checks, searchTerm]);

  const pagedChecks = useMemo(() => {
    const start = page * pageSize;
    return filteredChecks.slice(start, start + pageSize);
  }, [filteredChecks, page, pageSize]);

  useEffect(() => {
    if (!professionProfileId) return;
    refresh(professionProfileId);
  }, [professionProfileId, refresh]);

  useEffect(() => {
    setPage(0);
  }, [professionProfileId, searchTerm]);

  const handleCreateCheck = async () => {
    if (!professionProfileId) return;
    setCreateSubmitting(true);
    try {
      const values = await createForm.validateFields();
      await createForProfessionProfile(
        professionProfileId,
        {
          checkType: values.checkType,
          notes: values.notes || null,
        },
        actorId,
      );
      onCreateDrawerOpenChange?.(false);
      createForm.resetFields();
      onReloadProfile?.();
      await refresh(professionProfileId);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.message || "Failed to create background check");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const openCheckDetails = (record) => {
    navigate(paths.helperProfessionBackgroundCheckDetails(record.id), {
      state: {
        check: record,
        returnTo: paths.helperProfessionBackgroundChecks,
        returnState: {
          profile: profile || null,
          helperProfile: helperProfile || null,
        },
        display: {
          name:
            helperProfile?.fullName ||
            [helperProfile?.firstName, helperProfile?.middleName, helperProfile?.lastName]
              .filter(Boolean)
              .join(" ") ||
            helperProfile?.userFullName ||
            record?.userFullName ||
            record?.userName ||
            record?.fullName ||
            profile?.userFullName ||
            profile?.userName ||
            profile?.fullName ||
            "-",
          email:
            helperProfile?.email ||
            helperProfile?.userEmail ||
            record?.userEmail ||
            record?.email ||
            profile?.userEmail ||
            profile?.email ||
            "-",
          phone:
            helperProfile?.phone ||
            helperProfile?.userPhone ||
            record?.userPhone ||
            record?.phone ||
            profile?.userPhone ||
            profile?.phone ||
            "-",
          photo:
            helperProfile?.profilePhoto ||
            helperProfile?.photo ||
            record?.profilePhoto ||
            record?.photo ||
            profile?.profilePhoto ||
            profile?.photo ||
            null,
        },
        profile: profile || {
          id: professionProfileId,
          userId,
          profileName,
          helperProfile,
          sector: sectorLabel ? { name: sectorLabel } : undefined,
        },
      },
    });
  };

  const columns = [
    {
      title: "Check",
      dataIndex: "checkType",
      key: "checkType",
      width: 280,
      render: (value, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{value || "-"}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record?.notes || "No notes"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (value) => <Tag color={CHECK_STATUS_COLORS[value] || "default"}>{toLabel(value)}</Tag>,
    },
  
    {
      title: "Requested",
      dataIndex: "requestedAt",
      key: "requestedAt",
      width: 180,
      render: (value) => formatDateTime(value),
    },
    {
      title: "Updated",
      dataIndex: "completedAt",
      key: "completedAt",
      width: 180,
      render: (value, record) => formatDateTime(value || record?.updatedAt || record?.lastUpdatedAt),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Button
          type="text"
          icon={<EyeOutlined />}
          aria-label="View background check"
          onClick={(event) => {
            event.stopPropagation();
            openCheckDetails(record);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  return (
    <Card style={{ borderRadius: 14, border: "none", boxShadow: "none" }} styles={{ body: { padding: 12 } }}>
      <Row gutter={[10, 10]} style={{ marginBottom: 12 }}>
        <Col xs={24} md={12} lg={6}>
          <StatCard label="Total checks" value={checkStats.total} hint="All checks for this profile" />
        </Col>
        <Col xs={24} md={12} lg={6}>
          <StatCard label="Open" value={checkStats.open} hint="Requested, pending, in progress" tone="warning" />
        </Col>
        <Col xs={24} md={12} lg={6}>
          <StatCard label="Passed" value={checkStats.passed} hint="Checks that closed successfully" tone="success" />
        </Col>
        <Col xs={24} md={12} lg={6}>
          <StatCard label="Pass rate" value={`${checkStats.passRate}%`} hint="Based on closed checks" tone="success" />
        </Col>
      </Row>

      <Row gutter={[10, 10]} align="middle" style={{ marginBottom: 12, paddingTop: 13 }}>
        <Col xs={24} lg={6}>
          <Input
            allowClear
            prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
            placeholder="Search..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={{ borderRadius: 12 }}
          />
        </Col>
        <Col xs={24} lg={18} style={{ display: "flex", justifyContent: "flex-end" }}>
          {profile ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => onCreateDrawerOpenChange?.(true)}>
              Create
            </Button>
          ) : null}
        </Col>
      </Row>

      {error ? (
        <Card size="small" style={{ marginBottom: 12, borderRadius: 12, border: "none", boxShadow: "none" }}>
          <Text type="danger">{error}</Text>
        </Card>
      ) : null}

      <Table
        rowKey={(record) => String(record?.id)}
        loading={loading}
        columns={columns}
        dataSource={pagedChecks}
        onRow={(record) => ({
          onClick: () => openCheckDetails(record),
        })}
        pagination={{
          current: page + 1,
          pageSize,
          total: filteredChecks.length,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage - 1);
            setPageSize(nextPageSize);
          },
        }}
        tableLayout="fixed"
        scroll={{ x: "max-content" }}
        className="custom-table"
        size="small"
        locale={{
          emptyText: (
            <Empty
              description="No background checks found for this profile."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
      />

      <Drawer
        open={createDrawerOpen}
        title="Create background check"
        width={560}
        zIndex={1500}
        onClose={() => onCreateDrawerOpenChange?.(false)}
        destroyOnHidden
        styles={{ body: { paddingBottom: 96 } }}
        footer={
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={() => onCreateDrawerOpenChange?.(false)}>Cancel</Button>
            <Button type="primary" onClick={handleCreateCheck} loading={createSubmitting}>
              Create
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Card size="small" style={{ borderRadius: 12, border: "none", boxShadow: "none" }}>
            <Space direction="vertical" size={4}>
              <Text type="secondary">Profile</Text>
              <Text strong>{profileName}</Text>
              <Text type="secondary">{sectorLabel || "No sector provided"}</Text>
            </Space>
          </Card>
          <Form layout="vertical" form={createForm}>
            <Form.Item
              name="checkType"
              label="Check type"
              rules={[{ required: true, message: "Select a background check type" }]}
            >
              <Select options={BACKGROUND_CHECK_TYPES} placeholder="Select type" />
            </Form.Item>
            <Form.Item name="notes" label="Notes">
              <Input.TextArea rows={4} placeholder="Optional notes for the check" />
            </Form.Item>
          </Form>
        </Space>
      </Drawer>
    </Card>
  );
}
